// Builds the policy details model from the policy's `policyType` by
// looking up the form fields the input form would render
// (`getPolicyFormFields`) and pulling the corresponding values off the
// policy. Pure data-transformation — no React imports.

import { getPolicyFormFields, FORM_FIELD_COMPONENTS } from '@/components/InsuranceFormFields';
import { fundingPolicyTypeLabel } from '../../glossary/enumLabels';
import { descriptorsForFormFields } from './formFieldDescriptors';
import { EnvRow, FieldSpec } from './types';

export interface BuildPolicyDetailsInput {
  policy: PolicyFundingSource | null;
  /**
   * `clientHasStartedLtc` from the intake survey. When true, the form
   * also renders `BenefitUtilizedToDateField`; we mirror that here.
   */
  clientHasStartedLtc: boolean;
}

export interface PolicyDetailsModel {
  identityFields: FieldSpec[];
  formFields: FieldSpec[];
  envRows: EnvRow[];
  hasPerEnv: boolean;
}

function buildIdentityFields(policy: PolicyFundingSource | null): FieldSpec[] {
  if (!policy) return [];
  const carrier = (policy.sourceQuote as { carrierName?: string } | undefined)?.carrierName ?? null;
  return [
    { label: 'Custom name', value: policy.policyCustomName },
    { label: 'Carrier', value: carrier },
    {
      label: 'Policy type',
      value: fundingPolicyTypeLabel(policy.policyType as unknown as number),
      format: 'text',
    },
  ];
}

function buildEnvRows(p: PolicyFundingSource): EnvRow[] {
  return [
    {
      env: 'Home',
      daily: p.dailyHomeBenefitAmount,
      inflation: p.dailyHomeInflationProtectionPercent,
    },
    {
      env: 'Independent living',
      daily: p.dailyIndependentLivingBenefitAmount,
      inflation: p.dailyIndependentLivingInflationProtectionPercent,
    },
    {
      env: 'Assisted living',
      daily: p.dailyAssistedLivingBenefitAmount,
      inflation: p.dailyAssistedLivingInflationProtectionPercent,
    },
    {
      env: 'Nursing home',
      daily: p.dailyNursingHomeBenefitAmount,
      inflation: p.dailyNursingHomeInflationProtectionPercent,
    },
  ];
}

export function buildPolicyDetails(opts: BuildPolicyDetailsInput): PolicyDetailsModel {
  const { policy, clientHasStartedLtc } = opts;
  const identityFields = buildIdentityFields(policy);

  if (!policy?.policyType) {
    return { identityFields, formFields: [], envRows: [], hasPerEnv: false };
  }

  const formComponents = [
    ...getPolicyFormFields(policy.policyType),
    ...(clientHasStartedLtc ? [FORM_FIELD_COMPONENTS.BenefitUtilizedToDateField] : []),
  ];
  const descriptors = descriptorsForFormFields(formComponents);

  const formFields: FieldSpec[] = [];
  let hasPerEnv = false;
  for (const group of descriptors) {
    if (group.kind === 'perEnv') {
      hasPerEnv = true;
      continue;
    }
    for (const f of group.fields) {
      formFields.push({
        label: f.label,
        tooltip: f.tooltip,
        value: policy[f.key],
        format: f.format,
      });
    }
  }

  return {
    identityFields,
    formFields,
    envRows: hasPerEnv ? buildEnvRows(policy) : [],
    hasPerEnv,
  };
}
